import { useEffect, useRef } from 'react'
import * as d3 from 'd3'
import { useTheme } from '../../context/ThemeContext'

const DEFAULT_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6']

export default function HorizontalBarChart({
  data,
  valueKey = 'value',
  labelKey = 'label',
  colors = DEFAULT_COLORS,
  showValues = true,
  maxValue = null
}) {
  const containerRef = useRef(null)
  const { isDark } = useTheme()

  useEffect(() => {
    if (!containerRef.current || !data || data.length === 0) return

    d3.select(containerRef.current).selectAll('*').remove()

    const container = containerRef.current
    const width = container.clientWidth
    const height = container.clientHeight
    const margin = { top: 10, right: 60, bottom: 20, left: 100 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const svg = d3.select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // Scales
    const yScale = d3.scaleBand()
      .domain(data.map(d => d[labelKey]))
      .range([0, innerHeight])
      .padding(0.3)

    const xMax = maxValue || d3.max(data, d => d[valueKey]) * 1.1
    const xScale = d3.scaleLinear()
      .domain([0, xMax])
      .range([0, innerWidth])

    // Grid lines
    g.append('g')
      .attr('class', 'grid')
      .selectAll('line')
      .data(xScale.ticks(5))
      .join('line')
      .attr('x1', d => xScale(d))
      .attr('x2', d => xScale(d))
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', isDark ? '#374151' : '#e5e7eb')
      .attr('stroke-dasharray', '2,2')

    // Bars
    g.selectAll('.bar')
      .data(data)
      .join('rect')
      .attr('class', 'bar')
      .attr('x', 0)
      .attr('y', d => yScale(d[labelKey]))
      .attr('width', d => xScale(d[valueKey]))
      .attr('height', yScale.bandwidth())
      .attr('fill', (d, i) => colors[i % colors.length])
      .attr('rx', 4)

    // Value labels
    if (showValues) {
      g.selectAll('.value-label')
        .data(data)
        .join('text')
        .attr('class', 'value-label')
        .attr('x', d => xScale(d[valueKey]) + 8)
        .attr('y', d => yScale(d[labelKey]) + yScale.bandwidth() / 2)
        .attr('dy', '0.35em')
        .attr('fill', isDark ? '#d1d5db' : '#4b5563')
        .attr('font-size', '12px')
        .attr('font-weight', '500')
        .text(d => d[valueKey].toLocaleString())
    }

    // Y Axis (labels)
    g.append('g')
      .call(d3.axisLeft(yScale))
      .selectAll('text')
      .attr('fill', isDark ? '#d1d5db' : '#374151')
      .attr('font-size', '12px')

    g.select('.domain').remove()
    g.selectAll('.tick line').remove()

  }, [data, valueKey, labelKey, colors, showValues, maxValue, isDark])

  return (
    <div ref={containerRef} className="w-full h-full" />
  )
}
